import { Component, OnInit, ChangeDetectorRef, NgZone, LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { CommonModule } from '@angular/common';
import { ChildrenSelectorComponent, Student } from '../../shared/children-selector/children-selector.component';
import { TableModule } from 'primeng/table';
import { PaiementService } from '../../services/paiement.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { PageTitleComponent } from '../../shared/page-title/page-title.component';
import { LoaderComponent } from "../../shared/loader/loader.component";
import { DownloadFilesService } from '../../services/download-files.service';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../services/notification.service';

interface Payment {
  id: number;
  studentId: number;
  month: string;
  label: string;
  amount: number;
  status: 'Payé' | 'Non Payé' | 'Reçu';
  pathFile?: string;
  receiptNumber?: string | number;
  date?: string;
}

@Component({
  selector: 'app-paiement',
  standalone: true,
  providers: [
    { provide: LOCALE_ID, useValue: 'fr' }
  ],
  imports: [
    CommonModule,
    ChildrenSelectorComponent,
    TableModule,
    TranslateModule,
    PageTitleComponent,
    LoaderComponent,
    FormsModule
],
  templateUrl: './paiement.component.html',
  styleUrls: ['./paiement.component.css']
})
export class PaiementComponent implements OnInit {
  tabs = ['Payé', 'Non Payé', 'Reçu'] as const;
  selectedTab = 0;

  selectedStudent: Student | null = null;
  selectedStudentId: number | null = null;
isLoading = false; // 🔹 Indique si les paiements sont en cours de chargement

  payments: Payment[] = [];
  userId = Number(localStorage.getItem('userId')) || 0  ;
  private pendingRequests = 0;

  // Pagination for payment cards (3 per page)
  paymentsPerPage = 3;
  paidCurrentPage = 1;
  unpaidCurrentPage = 1;
  
  // Pagination for receipt columns
  receiptsColumnsPerPage = 3;
  receiptsCurrentPage = 1;
  // New simple table pagination for receipts
  receiptsPageSize = 10;
  receiptsCurrentPageTable = 1;

  private paginateChunks<T>(chunks: T[][], page: number, perPage: number): T[][] {
    const start = (page - 1) * perPage;
    const end = start + perPage;
    return chunks.slice(start, end);
  }

  getReceiptChunks(tab: 'Payé' | 'Non Payé' | 'Reçu', month: string): any[][] {
    const chunks = this.chunkArray(this.getPaymentsForMonth(tab, month), 5);
    return this.paginateChunks(chunks, this.receiptsCurrentPage, this.receiptsColumnsPerPage);
  }

  // --- New table-style pagination helpers for receipts ---
  getReceiptsForStudent(): Payment[] {
    if (!this.selectedStudentId) return [];
    return this.payments.filter(p => p.status === 'Reçu' && p.studentId === this.selectedStudentId);
  }

  paginatedReceiptsTable(): Payment[] {
    const all = this.getReceiptsForStudent();
    const start = (this.receiptsCurrentPageTable - 1) * this.receiptsPageSize;
    return all.slice(start, start + this.receiptsPageSize);
  }

  receiptsTotalPagesTable(): number {
    const total = this.getReceiptsForStudent().length;
    return Math.max(1, Math.ceil(total / this.receiptsPageSize));
  }

  goToReceiptsTablePage(page: number) {
    const total = this.receiptsTotalPagesTable();
    if (page >= 1 && page <= total) this.receiptsCurrentPageTable = page;
  }

  previousReceiptsTablePage() { this.goToReceiptsTablePage(this.receiptsCurrentPageTable - 1); }
  nextReceiptsTablePage() { this.goToReceiptsTablePage(this.receiptsCurrentPageTable + 1); }

  getReceiptTotalPages(tab: 'Payé' | 'Non Payé' | 'Reçu', month: string): number {
    const chunks = this.chunkArray(this.getPaymentsForMonth(tab, month), 5);
    return Math.max(1, Math.ceil(chunks.length / this.receiptsColumnsPerPage));
  }

  goToReceiptPage(page: number, tab: 'Payé' | 'Non Payé' | 'Reçu', month: string) {
    const total = this.getReceiptTotalPages(tab, month);
    if (page >= 1 && page <= total) {
      this.receiptsCurrentPage = page;
    }
  }

  previousReceiptPage(tab: 'Payé' | 'Non Payé' | 'Reçu', month: string) {
    this.goToReceiptPage(this.receiptsCurrentPage - 1, tab, month);
  }

  nextReceiptPage(tab: 'Payé' | 'Non Payé' | 'Reçu', month: string) {
    this.goToReceiptPage(this.receiptsCurrentPage + 1, tab, month);
  }

  // 🔧 Corrigé : URL sans espaces
  private readonly baseUrl = 'https://plateforme.educanet.pro';

  constructor(
    private downloadService: DownloadFilesService,
    private paiementService: PaiementService,
    private translate: TranslateService,
    private http: HttpClient,
    private notificationService: NotificationService,
    private cdRef: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    // Register French locale for Angular date pipes
    try {
      registerLocaleData(localeFr);
    } catch (e) {
      console.warn('Could not register French locale data:', e);
    }
    // 🔧 Langues supportées
    this.translate.addLangs(['fr', 'ar']);
    this.translate.setDefaultLang('fr'); // Langue par défaut

    const savedLang = localStorage.getItem('lang');
    const browserLang = this.translate.getBrowserLang();
    const langToUse = savedLang || (browserLang?.match(/fr|ar/) ? browserLang : 'fr');

    // 🔧 Charger la langue
    this.translate.use(langToUse).subscribe({
      error: err => console.error('Échec chargement traduction', err)
    });
  }

  ngOnInit(): void {}

  onStudentSelected(student: Student) {
    this.selectedStudent = student;
    this.selectedStudentId = student.idStudent;
    setTimeout(() => {
      this.cdRef.detectChanges(); // Trigger change detection to fix ExpressionChangedAfterItHasBeenCheckedError
    });
    this.loadPayments(student);
  }

  selectTab(index: number) {
    this.selectedTab = index;
    // Reset pagination when switching tabs
    this.paidCurrentPage = 1;
    this.unpaidCurrentPage = 1;
  }
  
  // Get paginated payments for Payé
  getPaginatedPaid(): Payment[] {
    if (!this.selectedStudentId) return [];
    const all = this.payments.filter(p => p.status === 'Payé' && p.studentId === this.selectedStudentId);
    const start = (this.paidCurrentPage - 1) * this.paymentsPerPage;
    return all.slice(start, start + this.paymentsPerPage);
  }
  
  // Get paginated payments for Non Payé
  getPaginatedUnpaid(): Payment[] {
    if (!this.selectedStudentId) return [];
    const all = this.payments.filter(p => p.status === 'Non Payé' && p.studentId === this.selectedStudentId);
    const start = (this.unpaidCurrentPage - 1) * this.paymentsPerPage;
    return all.slice(start, start + this.paymentsPerPage);
  }
  
  // Get total pages for Payé
  getTotalPagesPaid(): number {
    if (!this.selectedStudentId) return 1;
    const all = this.payments.filter(p => p.status === 'Payé' && p.studentId === this.selectedStudentId);
    return Math.max(1, Math.ceil(all.length / this.paymentsPerPage));
  }
  
  // Get total pages for Non Payé
  getTotalPagesUnpaid(): number {
    if (!this.selectedStudentId) return 1;
    const all = this.payments.filter(p => p.status === 'Non Payé' && p.studentId === this.selectedStudentId);
    return Math.max(1, Math.ceil(all.length / this.paymentsPerPage));
  }
  
  // Navigate paid pages
  goToPaidPage(page: number) {
    if (page >= 1 && page <= this.getTotalPagesPaid()) {
      this.paidCurrentPage = page;
    }
  }
  
  previousPaidPage() { this.goToPaidPage(this.paidCurrentPage - 1); }
  nextPaidPage() { this.goToPaidPage(this.paidCurrentPage + 1); }
  
  // Navigate unpaid pages
  goToUnpaidPage(page: number) {
    if (page >= 1 && page <= this.getTotalPagesUnpaid()) {
      this.unpaidCurrentPage = page;
    }
  }
  
  previousUnpaidPage() { this.goToUnpaidPage(this.unpaidCurrentPage - 1); }
  nextUnpaidPage() { this.goToUnpaidPage(this.unpaidCurrentPage + 1); }

  // Helper methods to check for data without filter in template
  hasPaidPayments(): boolean {
    if (!this.selectedStudentId) return false;
    return this.payments.some(p => p.status === 'Payé' && p.studentId === this.selectedStudentId);
  }

  hasUnpaidPayments(): boolean {
    if (!this.selectedStudentId) return false;
    return this.payments.some(p => p.status === 'Non Payé' && p.studentId === this.selectedStudentId);
  }

  getMonths(status: 'Payé' | 'Non Payé' | 'Reçu'): string[] {
    if (!this.selectedStudentId) return [];
    if (status === 'Reçu') {
      const receipts = this.payments.filter(p => p.status === 'Reçu' && p.studentId === this.selectedStudentId);
      return receipts.length > 0 ? ['Reçu'] : [];
    }
    const months = this.payments
      .filter(p => p.status === status && p.studentId === this.selectedStudentId)
      .map(p => p.month);
    return Array.from(new Set(months));
  }

  // Retourne l'affichage français du mois ou 'Frais d\'hiver' si pas de mois
  formatMonthDisplay(month?: string | null): string {
    if (!month || month.toString().trim() === '') return "Frais divers";
    const m = month.toString().trim().toLowerCase();
    const map: Record<string, string> = {
      january: 'janvier',
      february: 'février',
      march: 'mars',
      april: 'avril',
      may: 'mai',
      june: 'juin',
      july: 'juillet',
      august: 'août',
      september: 'septembre',
      october: 'octobre',
      november: 'novembre',
      december: 'décembre',
      jan: 'janvier',
      feb: 'février',
      mar: 'mars',
      apr: 'avril',
      may_short: 'mai',
      jun: 'juin',
      jul: 'juillet',
      aug: 'août',
      sep: 'septembre',
      oct: 'octobre',
      nov: 'novembre',
      dec: 'décembre'
    };

    // If month comes as e.g. 'September' or 'SEPTEMBER' or localized variants
    if (map[m]) return map[m];

    // Try to detect by startsWith for languages like 'september', 'septembre'
    const starts = Object.keys(map).find(k => m.startsWith(k));
    if (starts) return map[starts];

    // If it's already a french month spelled correctly, return with capitalization
    const frenchMonths = Object.values(map);
    if (frenchMonths.includes(month.toLowerCase())) return month.charAt(0).toUpperCase() + month.slice(1);

    // Fallback: capitalize first letter
    return month.charAt(0).toUpperCase() + month.slice(1);
  }

  getPaymentsForMonth(status: 'Payé' | 'Non Payé' | 'Reçu', month?: string) {
    if (!this.selectedStudentId) return [];
    if (status === 'Reçu') {
      return this.payments.filter(p => p.status === 'Reçu' && p.studentId === this.selectedStudentId);
    }
    return this.payments.filter(
      p => p.status === status && p.month === month && p.studentId === this.selectedStudentId
    );
  }

  getMonthTotal(status: 'Payé' | 'Non Payé' | 'Reçu', month: string): number {
    return this.getPaymentsForMonth(status, month).reduce((sum, p) => sum + p.amount, 0);
  }

  chunkArray<T>(arr: T[], size: number): T[][] {
    const result: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  }


  downloadPDF(filePath: string, filename: string, payment?: Payment) {
    // Validate filePath
    if (!filePath || filePath.trim() === '') {
      console.error('downloadPDF: filePath is empty');
      this.notificationService.showNotification('error', 'Le chemin du fichier est vide');
      return;
    }

    console.log('downloadPDF called with:', { filePath, filename });

    // Marquer comme vu uniquement pour les reçus si on dispose de l'id et de l'étudiant
    if (payment && payment.status === 'Reçu' && this.selectedStudentId && payment.id) {
      this.notificationService.updateNotificationStatus({
        idType: payment.id,
        idStudent: this.selectedStudentId,
        nameType: 'Payment'
      }).subscribe({
        next: () => {
          this.notificationService.getNotificationCounts(this.userId.toString()).subscribe();
        },
        error: (err) => console.error('Erreur updatenotif (Payment):', err)
      });
    }

    // If running in browser, attempt to download the file
    try {
      if (typeof window !== 'undefined') {
        let fullUrl = filePath;
        
        // Replace incorrect domain with correct one
        if (fullUrl.includes('parent2026.educanet.pro')) {
          fullUrl = fullUrl.replace('https://parent2026.educanet.pro', 'https://plateforme.educanet.pro');
        }
        
        // If path is relative (doesn't start with http), construct full URL
        if (!fullUrl.startsWith('http')) {
          const cleanPath = fullUrl.startsWith('/') ? fullUrl.substring(1) : fullUrl;
          fullUrl = `https://plateforme.educanet.pro/${cleanPath}`;
        }
        
        // Only add .pdf extension if the URL doesn't already end with a file extension
        if (!(/\.\w+$/.test(fullUrl))) {
          fullUrl += '.pdf';
        }
        
        console.log('Opening file with window.open:', fullUrl);
        
        // Use window.open to bypass CORS issues
        // This opens the file in a new tab/window which doesn't have CORS restrictions
        window.open(fullUrl, '_blank');
        this.notificationService.showNotification('success', 'Fichier ouvert dans une nouvelle fenêtre');
      }
    } catch (e) {
      console.error('Unexpected error in downloadPDF:', e);
      this.notificationService.showNotification('error', 'Une erreur inattendue s\'est produite');
    }
  }



  private loadPayments(student: Student) {
    setTimeout(() => {
      this.isLoading = true;
    }, 0);

    // --- Paiements Payés ---
    this.pendingRequests++;
    this.paiementService.getPaidPayments(student.idStudent, student.idOrg).subscribe({
      next: (res: any) => {
        // API returns { error, response: [...] }
        const list = res?.response || [];
        const paid: Payment[] = [];

        list.forEach((item: any) => {
          const month = item.mois;
          (item.listDetailPaiement?.detailPaiementMobile || []).forEach((d: any) => {
            paid.push({
              id: d.id || Date.now(),
              studentId: student.idStudent,
              month,
              label: d.name,
              amount: d.amount || 0,
              status: 'Payé'
            });
          });
        });

        this.updatePayments('Payé', paid);
      },
      error: (err) => console.error('Erreur chargement paiements payés', err),
      complete: () => {
        if (--this.pendingRequests === 0) {
          this.ngZone.run(() => {
            this.isLoading = false;
            this.cdRef.detectChanges();
          });
        }
      }
    });

    // --- Non Payés ---
    this.pendingRequests++;
    this.paiementService.getNotPaidPayments(student.idStudent, student.idOrg).subscribe({
      next: (res: any) => {
        const list = res?.response || [];
        const notPaid: Payment[] = [];

        list.forEach((item: any) => {
          const month = item.mois;
          (item.listDetailPaiement?.detailPaiementMobile || []).forEach((d: any) => {
            notPaid.push({
              id: d.id || Date.now(),
              studentId: student.idStudent,
              month,
              label: d.name,
              amount: d.amount || 0,
              status: 'Non Payé'
            });
          });
        });

        this.updatePayments('Non Payé', notPaid);
        this.cdRef.detectChanges();
      },
      error: (err) => console.error('Erreur chargement non payés', err),
      complete: () => {
        if (--this.pendingRequests === 0) {
          this.ngZone.run(() => {
            this.isLoading = false;
            this.cdRef.detectChanges();
          });
        }
      }
    });

    // --- Reçus ---
    this.pendingRequests++;
    this.paiementService.getRecuPaiement(student.idStudent, student.idOrg, 1, 100)
      .subscribe({
        next: (res: any) => {
          console.log('[PaiementComponent] Réponse reçus API:', res);
          console.log('[PaiementComponent] res.response:', res.response);
          
          const receipts: Payment[] = Array.isArray(res.response)
              ? res.response.map((r: any) => {
                  console.log('[PaiementComponent] Reçu loggé:', {
                    fileName: r.fileName,
                    fileNameRecu: r.fileNameRecu,
                    pathFile: r.pathFile,
                    pathfile: r.pathfile,
                    path: r.path,
                    url: r.url,
                    fileUrl: r.fileUrl,
                    downloadUrl: r.downloadUrl,
                    lien: r.lien,
                    toutes_les_cles: Object.keys(r)
                  });
                  
                  return {
                    id: r.idhistorique || r.idRecu || Date.now(),
                    studentId: student.idStudent,
                    month: 'Reçu',
                    label: r.fileName || r.fileNameRecu || 'Reçu',
                    // Prefer the API's 'montant' field, fall back to common alternatives
                    amount: (r.montant !== undefined && r.montant !== null) ? r.montant : (r.total !== undefined ? r.total : 0),
                    status: 'Reçu',
                    pathFile: r.pathFile || r.pathfile || r.path || r.url || r.fileUrl || r.downloadUrl || r.lien || '',
                    // Use idRecu/idhistorique as receipt number when present
                    receiptNumber: r.idRecu || r.idhistorique || (r.fileName ? (r.fileName.match(/(\d{2,})/) || [])[0] : undefined),
                    // Prefer dateRecu, then other possible fields
                    date: r.dateRecu || r.creationDate || r.date || r.datePaiement || r.dateString || ''
                  };
                })
              : [];

          console.log('[PaiementComponent] Reçus traités:', receipts);
          this.updatePayments('Reçu', receipts);
          this.cdRef.detectChanges();
        },
        error: (err) => console.error('Erreur chargement reçus', err),
        complete: () => {
          if (--this.pendingRequests === 0) {
            this.ngZone.run(() => {
              this.isLoading = false;
              this.cdRef.detectChanges();
            });
          }
        }
      });
  }

  cleanReceiptLabel(label: string): string {
    // Supprime tout après le premier _ après "Recu"
    // Ex: Recu_107_Selim Douma.pdf -> Recu_107
    if (!label) return '';
    const parts = label.split('_');
    return parts.length > 1 ? `${parts[0]}_${parts[1]}` : label;
  }

  // 🔧 Mise à jour centralisée des paiements
  private updatePayments(status: 'Payé' | 'Non Payé' | 'Reçu', newPayments: Payment[]) {
    this.payments = [
      ...this.payments.filter(p => !(p.status === status && p.studentId === this.selectedStudentId)),
      ...newPayments
    ];
  }

}